/**
 * /settings/system-flags
 *
 * Super-Admin-only console for flipping the `danger_tools_enabled` feature
 * flag. When ON, the Legacy Migration page + the Danger Zone section of
 * Settings reappear. When OFF (post-go-live default), both surfaces are
 * hidden in the UI and the backend returns 403 even if URLs are hit
 * directly.
 *
 * To prevent fat-finger accidents, the toggle is gated by a typed-confirm
 * dialog ("ENABLE" / "DISABLE"). Every flip is captured server-side in
 * `app_settings_audit_log`.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, ShieldCheck, ShieldAlert, Loader2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useAuth } from '../context/AuthContext';


// Child component — gives the lint pass a fresh scope so the audit fetch
// effect doesn't get tangled with the parent's setState callers.
const FeatureFlagAuditLog = ({ reloadKey }) => {
  const [rows, setRows] = useState(null); // null=loading, []=empty

  useEffect(() => {
    let cancelled = false;
    api.get('/settings/audit/feature-flags')
      .then(r => { if (!cancelled && r.data?.success) setRows(r.data.data || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  return (
    <Card data-testid="flag-audit-log">
      <CardHeader>
        <CardTitle className="text-base">Recent changes</CardTitle>
        <CardDescription>Every flip is recorded server-side.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No changes yet.</p>
        ) : (
          <ul className="space-y-2 text-sm" data-testid="flag-audit-list">
            {rows.slice(0, 20).map((e, i) => (
              <li key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
                <div>
                  <span className="font-medium">{e.key}</span>
                  <span className="text-muted-foreground"> · {String(e.previous_value)} → </span>
                  <span className={e.new_value ? 'text-emerald-500 font-medium' : 'text-muted-foreground font-medium'}>{String(e.new_value)}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {e.changed_by_name || e.changed_by || '—'} · {new Date(e.timestamp).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const SystemFlagsPage = () => {
  const { user } = useAuth();
  const { dangerToolsEnabled } = useFeatureFlags();
  const [pending, setPending] = useState(null); // 'enable' | 'disable' | null
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [auditReloadKey, setAuditReloadKey] = useState(0);
  const [pfEnabled, setPfEnabled] = useState(null);
  const [pfSaving, setPfSaving] = useState(false);
  const [launchEnabled, setLaunchEnabled] = useState(null);
  const [launchSaving, setLaunchSaving] = useState(false);

  // Super Admin = user holds the `*` wildcard permission. This matches
  // the backend's auth model (any role whose slug is `super_admin` is
  // auto-upgraded to `*` in `_user_permissions`).
  const isSuperAdmin = !!user?.permissions?.includes('*');

  // Fetch PF flag once on mount. We use the public flag-snapshot endpoint
  // which already includes `pf_enabled` alongside `danger_tools_enabled`.
  useEffect(() => {
    api.get('/settings/flags')
      .then(r => {
        if (r.data?.success) {
          setPfEnabled(!!r.data.data?.pf_enabled);
          // launch_email_blast_enabled defaults to true server-side.
          setLaunchEnabled(r.data.data?.launch_email_blast_enabled !== false);
        }
      })
      .catch(() => { setPfEnabled(false); setLaunchEnabled(true); });
  }, [auditReloadKey]);

  const togglePf = async (next) => {
    setPfSaving(true);
    try {
      const r = await api.put('/settings/flags/pf-enabled', { enabled: next });
      if (r.data?.success) {
        setPfEnabled(!!r.data.data.pf_enabled);
        toast.success(r.data.message || 'PF setting updated');
        setAuditReloadKey(k => k + 1);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setPfSaving(false); }
  };

  const toggleLaunch = async (next) => {
    setLaunchSaving(true);
    try {
      const r = await api.put('/settings/flags/launch-email-blast', { enabled: next });
      if (r.data?.success) {
        setLaunchEnabled(!!r.data.data.launch_email_blast_enabled);
        toast.success(r.data.message || 'Launch Email Blast setting updated');
        setAuditReloadKey(k => k + 1);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setLaunchSaving(false); }
  };

  const requestToggle = (turnOn) => {
    setPending(turnOn ? 'enable' : 'disable');
    setConfirm('');
  };

  const expected = pending === 'enable' ? 'ENABLE' : 'DISABLE';
  const canSubmit = confirm.trim().toUpperCase() === expected && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.put('/settings/flags/danger-tools', {
        enabled: pending === 'enable',
        confirm: confirm.trim().toUpperCase(),
      });
      if (r.data?.success) {
        toast.success(r.data.message || 'Flag updated');
        setPending(null);
        setConfirm('');
        // Bumping the reload key triggers the audit child to re-fetch.
        setAuditReloadKey(k => k + 1);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <ShieldAlert className="h-10 w-10 mx-auto mb-3 text-amber-500" />
            <p className="font-medium">Super Admin role required</p>
            <p className="text-sm mt-2">System Flags can only be changed by a Super Admin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">System Flags</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Runtime feature toggles. Changes take effect immediately for new requests and within ~60s on every open browser tab.
        </p>
      </div>

      <Card data-testid="danger-tools-flag-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Legacy Migration & Danger Zone
              </CardTitle>
              <CardDescription>
                Hides both destructive surfaces from the sidebar and blocks their backend APIs (403) when OFF. Default after go-live is OFF.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3" data-testid="danger-tools-toggle-area">
              <span className={`text-xs font-medium px-2 py-1 rounded ${dangerToolsEnabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                {dangerToolsEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <Switch
                checked={dangerToolsEnabled}
                onCheckedChange={(v) => requestToggle(v)}
                data-testid="danger-tools-switch"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`rounded-md border p-3 text-sm ${dangerToolsEnabled ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-muted bg-muted/20'}`}>
            <div className="flex items-start gap-2">
              {dangerToolsEnabled
                ? <ShieldCheck className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                : <ShieldAlert className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />}
              <div>
                <p className="font-medium">
                  {dangerToolsEnabled ? 'Migration + Danger Zone are visible to users with the right permissions.' : 'Migration + Danger Zone are hidden everywhere.'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Best practice: flip ON only for the duration of the data event (re-import, purge, etc.), then flip OFF.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="pf-enabled-flag-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                Statutory PF Deductions
              </CardTitle>
              <CardDescription>
                Apply 12% × min(basic, ₹15,000) Provident Fund deduction in payroll. India's EPF Act becomes mandatory once you employ <strong>20+ people</strong> — keep this OFF until then, or flip ON to enrol voluntarily.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-medium px-2 py-1 rounded ${pfEnabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                {pfEnabled === null ? '…' : pfEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <Switch
                checked={!!pfEnabled}
                onCheckedChange={togglePf}
                disabled={pfEnabled === null || pfSaving}
                data-testid="pf-enabled-switch"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card data-testid="launch-email-blast-flag-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Launch Email Blast
              </CardTitle>
              <CardDescription>
                Master switch for the one-time &ldquo;We&apos;re live!&rdquo; welcome email under Settings → Launch Email Blast. When OFF, both the bulk-send and the Send Test Email controls are hidden in Settings and the backend refuses the API calls. Default: ON.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-medium px-2 py-1 rounded ${launchEnabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                {launchEnabled === null ? '…' : launchEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <Switch
                checked={!!launchEnabled}
                onCheckedChange={toggleLaunch}
                disabled={launchEnabled === null || launchSaving}
                data-testid="launch-email-blast-switch"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Audit log — extracted into a child component to keep the lint
          analyzer's effect → setState data flow simple. */}
      <FeatureFlagAuditLog reloadKey={auditReloadKey} />

      {/* Typed-confirm dialog */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v) { setPending(null); setConfirm(''); } }}>
        <DialogContent data-testid="flag-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${pending === 'enable' ? 'text-amber-500' : 'text-emerald-500'}`} />
              {pending === 'enable' ? 'Enable Danger Tools?' : 'Disable Danger Tools?'}
            </DialogTitle>
            <DialogDescription>
              {pending === 'enable'
                ? 'This will re-expose the Legacy Migration page and the Danger Zone purge controls. Both can wipe data permanently. Proceed only if you are about to run a planned data event.'
                : 'This will hide the Legacy Migration page and the Danger Zone purge controls everywhere. Recommended state for normal operations.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm">Type <strong>{expected}</strong> to confirm:</label>
            <Input value={confirm} onChange={e => setConfirm(e.target.value)} autoFocus data-testid="flag-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPending(null); setConfirm(''); }} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={!canSubmit} data-testid="flag-confirm-submit"
              className={pending === 'enable' ? 'bg-red-500 hover:bg-red-600 text-white' : ''}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {pending === 'enable' ? 'Enable' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemFlagsPage;
